// [S-092] Attestation records data hooks.
//
// Backs apps/web/src/screens/Attestations.tsx (list) and
// AttestationDetail.tsx (single record). Reads from `/admin/attestations`
// — the server scopes by role (platform_admin → L1, tenant_admin → own L2,
// tenant_user → 403). Filters arrive as query params; we re-fetch when
// they change so the table reflects the current search input.
import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

export type AttestationRow = {
  readonly templateId: string;
  readonly tenantId: string | null;
  readonly layerSource: "L1" | "L2";
  readonly dealType: string;
  readonly guild: string;
  readonly tier: string;
  readonly lifecycleState: "certified" | "re_certified";
  readonly anchorCBA: string | null;
  readonly attestedBy: string;
  readonly attestedAt: string;
  readonly reAttestationDueAt: string | null;
  readonly preImageHash: string;
  // [S-221] Peer-review handoff metadata.
  readonly peerReviewerUid?: string | null;
  readonly peerReviewedAt?: string | null;
};

export type AttestationDetail = AttestationRow & {
  readonly content: string;
};

export type AttestationsListResponse = {
  attestations: AttestationRow[];
};

export type AttestationsFilter = {
  attorney?: string;
  cba?: string;
  dateFrom?: string;
  dateTo?: string;
};

export const ATTESTATIONS_KEY = ["admin", "attestations"] as const;

export function useAttestationsList(
  filter: AttestationsFilter
): UseQueryResult<AttestationsListResponse, Error> {
  const api = useApi("admin");
  const qs = buildQuery(filter);
  return useQuery({
    queryKey: [...ATTESTATIONS_KEY, "list", filter],
    queryFn: () =>
      api<AttestationsListResponse>(
        qs.length === 0 ? "/admin/attestations" : `/admin/attestations?${qs}`
      ),
    // Keep the previous filter's data on screen while a new filter
    // re-fetches — otherwise typing into a search input unmounts the
    // input itself (the screen's loading branch fires for every new
    // query key), which interrupts mid-stream keystrokes.
    placeholderData: keepPreviousData,
  });
}

export function useAttestationDetail(
  templateId: string
): UseQueryResult<AttestationDetail, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...ATTESTATIONS_KEY, "detail", templateId],
    queryFn: () =>
      api<AttestationDetail>(
        `/admin/attestations/${encodeURIComponent(templateId)}`
      ),
    enabled: templateId.length > 0,
  });
}

function buildQuery(f: AttestationsFilter): string {
  const params = new URLSearchParams();
  if (f.attorney !== undefined && f.attorney.length > 0) {
    params.set("attorney", f.attorney);
  }
  if (f.cba !== undefined && f.cba.length > 0) {
    params.set("cba", f.cba);
  }
  if (f.dateFrom !== undefined && f.dateFrom.length > 0) {
    params.set("dateFrom", f.dateFrom);
  }
  if (f.dateTo !== undefined && f.dateTo.length > 0) {
    params.set("dateTo", f.dateTo);
  }
  return params.toString();
}

/**
 * Recompute SHA-256 over the template content client-side and return it
 * as a lowercase hex digest — used by the AttestationDetail "Verify chain"
 * button to prove the server-returned hash matches the actual content.
 *
 * Uses the Web Crypto API (`crypto.subtle.digest`) which is available in
 * every supported browser and in Vitest's jsdom environment under Node 20+.
 */
export async function recomputeContentHash(content: string): Promise<string> {
  const bytes = new TextEncoder().encode(content);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
